import { ButtonGradientPrimary, FOCUS_RING, FOCUS_RING_NO_RADIUS, Icon } from '@amelio/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DynamicTimeline } from './DynamicTimeline';
import { GROWTH_GLASS_SURFACE, GROWTH_PLACEHOLDER, GROWTH_SECTION_TITLE } from './growthStyles';
import { computeTimelineLayout, TIMELINE } from './timelineGeometry';

import type { CareerStep } from '@/types/career-growth';

import { useAmelioTranslations } from '@/hooks';

/** Slack the browser leaves on `scrollLeft`, below which an arrow would scroll nowhere. */
const SCROLL_EPSILON = 4;

/** One click on an arrow moves the chart by one card, so no card is ever skipped. */
const SCROLL_STEP = TIMELINE.cardWidth + TIMELINE.cardGap;

/** The round scroll arrows: a 32px target, so they stay comfortably above the 24px minimum. */
const ARROW_BUTTON = `absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-growth-line bg-white shadow-sm ${FOCUS_RING_NO_RADIUS}`;

interface CareerPathTimelineProps {
    steps: CareerStep[];
    onEditSteps: () => void;
    dataTestId?: string;
}

/**
 * The "Parcours de carrière" section: the chart of the employee's steps, scrollable when the
 * career is longer than the panel, and the button that opens the steps editor.
 */
export const CareerPathTimeline = ({
    steps,
    onEditSteps,
    dataTestId = 'career-path-timeline',
}: CareerPathTimelineProps) => {
    const { t } = useAmelioTranslations();
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    /** `0` until the panel has been measured: the chart then draws at its narrowest. */
    const [containerWidth, setContainerWidth] = useState(0);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    // The steps the chart was last brought back to today for: a resize redraws the chart but
    // keeps the reader's place.
    const scrolledForSteps = useRef<CareerStep[] | null>(null);

    const layout = useMemo(
        () => computeTimelineLayout(steps, new Date(), containerWidth),
        [steps, containerWidth],
    );
    const hasChart = layout !== null;

    const updateArrows = useCallback(() => {
        const element = scrollRef.current;

        if (!element) {
            return;
        }

        setCanScrollLeft(element.scrollLeft > SCROLL_EPSILON);
        setCanScrollRight(
            element.scrollLeft + element.clientWidth < element.scrollWidth - SCROLL_EPSILON,
        );
    }, []);

    // The axis stretches to the panel, so the panel has to be measured before the chart is drawn.
    useEffect(() => {
        const element = scrollRef.current;

        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(([entry]) =>
            setContainerWidth(entry.contentRect.width),
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, [hasChart]);

    useEffect(() => {
        const element = scrollRef.current;

        if (!element) {
            return;
        }

        // The chart opens on today: the current step and the target are what the manager reads.
        if (scrolledForSteps.current !== steps) {
            scrolledForSteps.current = steps;
            element.scrollLeft = element.scrollWidth;
        }
        updateArrows();
    }, [steps, layout, updateArrows]);

    const scrollBy = (delta: number) =>
        scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

    return (
        <section
            className={`${GROWTH_GLASS_SURFACE} flex h-full min-w-0 flex-col gap-[20px] px-4 py-[20px] md:gap-[32px] md:px-12 md:py-[24px]`}
            data-testid={dataTestId}
        >
            <h3 className={GROWTH_SECTION_TITLE}>
                {t('performanceProfile.growth.timeline.title')}
            </h3>

            <div className={'flex min-w-0 flex-1 flex-col gap-[20px]'}>
                {layout ? (
                    <div className={'relative'}>
                        <DynamicTimeline
                            layout={layout}
                            scrollRef={scrollRef}
                            onScroll={updateArrows}
                            dataTestId={dataTestId}
                        />

                        {canScrollLeft && (
                            <button
                                type={'button'}
                                onClick={() => scrollBy(-SCROLL_STEP)}
                                aria-label={t('performanceProfile.growth.timeline.scrollLeft')}
                                className={`${ARROW_BUTTON} left-1`}
                                data-testid={`${dataTestId}-scroll-left`}
                            >
                                <Icon
                                    name={'arrow-arrow-left'}
                                    size={'sm'}
                                    ariaHidden
                                />
                            </button>
                        )}
                        {canScrollRight && (
                            <button
                                type={'button'}
                                onClick={() => scrollBy(SCROLL_STEP)}
                                aria-label={t('performanceProfile.growth.timeline.scrollRight')}
                                className={`${ARROW_BUTTON} right-1`}
                                data-testid={`${dataTestId}-scroll-right`}
                            >
                                <Icon
                                    name={'arrow-arrow-right'}
                                    size={'sm'}
                                    ariaHidden
                                />
                            </button>
                        )}
                    </div>
                ) : (
                    /* The whole panel is the invitation: a career with no step has nothing to
                       read, so the empty area does the job the button did, on its full surface. */
                    <button
                        type={'button'}
                        onClick={onEditSteps}
                        aria-label={t('performanceProfile.growth.timeline.addFirstStep')}
                        className={`border-growth-line hover:border-primary flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed transition-colors ${FOCUS_RING}`}
                        style={{ minHeight: TIMELINE.height }}
                        data-testid={`${dataTestId}-add-first-step`}
                    >
                        <span
                            className={
                                'bg-primary/10 text-primary flex h-12 w-12 items-center justify-center rounded-full'
                            }
                        >
                            <Icon
                                name={'essential-add'}
                                size={'md'}
                                color={'inherit'}
                                ariaHidden
                            />
                        </span>

                        <span className={GROWTH_PLACEHOLDER}>
                            {t('performanceProfile.growth.timeline.empty')}
                        </span>
                    </button>
                )}

                {hasChart && (
                    <ButtonGradientPrimary
                        type={'button'}
                        onClick={onEditSteps}
                        size={'sm'}
                        fitContent
                        className={'self-start'}
                        dataTestId={`${dataTestId}-edit`}
                    >
                        {t('performanceProfile.growth.timeline.editSteps')}
                    </ButtonGradientPrimary>
                )}
            </div>
        </section>
    );
};
